import { useContent } from "contexts/contentContext/contentContext";
import Image from "components/Elements/Image/Image";
import RecipientInput from "./RecipientInput";
import RecipientActions from "./RecipientActions";
import { useDrag, useDrop } from "react-dnd";
import { useRef, useEffect, useState } from "react";

const MAX_NAME_LENGTH = 50;
const MAX_EMAIL_LENGTH = 50;
const ITEM_TYPE = "recipient";

const RecipientItem = ({
  recipient,
  index,
  moveRecipient,
  activeRecipientId,
  setActiveRecipientId,
  tempField,
  setTempField,
  setfieldChanged,
  setUpdateToggle,
  validationErrors,
  setValidationErrors,
  handleNameChange,
  handleEmailChange,
  isNewRecipient,
  handleDeleteEmptyItem,
  handleDeleteRecipient,
}) => {
  const { getTextByKey } = useContent();
  const ref = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const [{ handlerId }, drop] = useDrop({
    accept: ITEM_TYPE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current || isNewRecipient) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      if (hoverIndex === -1) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveRecipient(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: () => {
      return { id: recipient.id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: (monitor) => {
      // Allow drag only when clicking outside of inputs and not on a new recipient
      const target = document.activeElement;
      const clickTarget = document.elementFromPoint(
        monitor.getClientOffset()?.x || 0,
        monitor.getClientOffset()?.y || 0
      );

      const isInputOrChild =
        clickTarget?.closest("input") || target.matches("input");
      return !isNewRecipient && !isInputOrChild;
    },
  });

  const opacity = isDragging ? 0.4 : 1;
  const dragRef = useRef(null);
  drag(dragRef);

  const handleRefs = (el) => {
    dragRef.current = el;
    ref.current = el;
    drop(el);
  };

  const handleRecipientClick = () => {
    if (activeRecipientId !== recipient.id) {
      setValidationErrors({});
      setActiveRecipientId(recipient.id);
    }
  };

  // Ensure component becomes active when inputs are focused
  useEffect(() => {
    const handleFocus = () => {
      if (activeRecipientId !== recipient.id) {
        setValidationErrors({});
        setActiveRecipientId(recipient.id);
      }
    };

    const inputs = ref.current?.querySelectorAll("input");
    inputs?.forEach((input) => {
      input.addEventListener("focus", handleFocus);
    });

    return () => {
      inputs?.forEach((input) => {
        input.removeEventListener("focus", handleFocus);
      });
    };
  }, [recipient.id, activeRecipientId, setActiveRecipientId]);

  const onNameChange = (value) => {
    if (isNewRecipient) {
      handleNameChange(value);
    } else if (value.length <= MAX_NAME_LENGTH) {
      const newRecipients = [...tempField.fields.recipients];
      const recipientIndex = newRecipients.findIndex(
        (r) => r.id === recipient.id
      );
      if (recipientIndex !== -1) {
        newRecipients[recipientIndex].name = value;
        setTempField({
          ...tempField,
          fields: {
            ...tempField.fields,
            recipients: newRecipients,
          },
        });
        setfieldChanged(true);
        setUpdateToggle(true);

        if (validationErrors.name) {
          setValidationErrors((prev) => ({ ...prev, name: false }));
        }
      }
    }
  };

  const onEmailChange = (value) => {
    if (isNewRecipient) {
      handleEmailChange(value);
    } else if (value.length <= MAX_EMAIL_LENGTH) {
      const newRecipients = [...tempField.fields.recipients];
      const recipientIndex = newRecipients.findIndex(
        (r) => r.id === recipient.id
      );
      if (recipientIndex !== -1) {
        newRecipients[recipientIndex].email = value;
        setTempField({
          ...tempField,
          fields: {
            ...tempField.fields,
            recipients: newRecipients,
          },
        });
        setfieldChanged(true);
        setUpdateToggle(true);

        if (validationErrors.email) {
          setValidationErrors((prev) => ({ ...prev, email: false }));
        }
      }
    }
  };

  const onDeleteClick = () => {
    if (isNewRecipient) {
      handleDeleteEmptyItem();
    } else {
      handleDeleteRecipient(recipient.id);
    }
  };

  return (
    <li
      ref={handleRefs}
      className={`recipient-item ${
        activeRecipientId === recipient.id ? "active" : ""
      }`}
      onClick={handleRecipientClick}
      style={{ opacity }}
      data-handler-id={handlerId}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="recipient-fields">
        <RecipientInput
          type="text"
          placeholder={`${getTextByKey("form-field-name")} ${
            !isNewRecipient
              ? index + 1
              : tempField.fields.recipients?.length + 1 || 1
          }`}
          value={recipient.name}
          onChange={(e) => onNameChange(e.target.value)}
          error={validationErrors.name}
          isActive={activeRecipientId === recipient.id}
          maxLength={MAX_NAME_LENGTH}
          currentLength={recipient.name.length}
          index={index}
        />
        <RecipientInput
          type="email"
          placeholder={getTextByKey("form-field-email")}
          value={recipient.email}
          onChange={(e) => onEmailChange(e.target.value)}
          error={validationErrors.email}
          isActive={activeRecipientId === recipient.id}
          maxLength={MAX_EMAIL_LENGTH}
          currentLength={recipient.email.length}
          index={index}
        />
        <Image name="connect-lines" ext="svg" className="connect-lines" />
      </div>

      <RecipientActions
        activeRecipientId={activeRecipientId}
        recipientId={recipient.id}
        handleDeleteRecipient={onDeleteClick}
        isHovered={isHovered}
      />
    </li>
  );
};

export default RecipientItem;
